import app from 'flarum/forum/app';
const TURNSTILE_SCRIPT_ID = 'flectar-turnstile-api';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_LOAD_TIMEOUT = 15_000;
let turnstileReady;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(true);
  if (turnstileReady) return turnstileReady;
  const load = new Promise(resolve => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existingScript && !(existingScript instanceof HTMLScriptElement)) {
      resolve(false);
      return;
    }
    const script = existingScript ?? document.createElement('script');
    let settled = false;
    const timeout = window.setTimeout(() => finish(false), TURNSTILE_LOAD_TIMEOUT);
    const finish = ready => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      if (!ready) script.remove();
      resolve(ready);
    };
    const onLoad = () => finish(!!window.turnstile);
    const onError = () => finish(false);
    script.addEventListener('load', onLoad, {
      once: true
    });
    script.addEventListener('error', onError, {
      once: true
    });
    if (!existingScript) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  turnstileReady = load.then(ready => {
    if (!ready) turnstileReady = undefined;
    return ready;
  });
  return turnstileReady;
}
export default class TurnstileState {
  token = null;
  renderGeneration = 0;
  constructor(callback, errorCallback) {
    this.callback = callback;
    this.errorCallback = errorCallback;
  }
  async render(element, theme = 'auto') {
    const generation = ++this.renderGeneration;
    const ready = await loadTurnstile();
    if (generation !== this.renderGeneration || !element?.isConnected) return;
    if (!ready || !window.turnstile) {
      this.reportError('flectar-turnstile.forum.not_loaded_error');
      return;
    }
    try {
      this.widgetId = window.turnstile.render(element, {
        sitekey: app.forum.attribute('flectar-turnstile.site_key'),
        theme,
        size: 'flexible',
        callback: token => {
          if (generation !== this.renderGeneration) return;
          this.token = token;
          this.callback(token);
        },
        'expired-callback': () => {
          if (generation === this.renderGeneration) this.token = null;
        },
        'error-callback': () => {
          if (generation === this.renderGeneration) {
            this.reportError('flectar-turnstile.forum.error');
          }
        }
      });
    } catch {
      this.reportError('flectar-turnstile.forum.error');
    }
  }
  getResponse() {
    return this.token;
  }
  reset() {
    this.token = null;
    if (this.widgetId !== undefined && window.turnstile) {
      try {
        window.turnstile.reset(this.widgetId);
      } catch {
        this.widgetId = undefined;
      }
    }
  }
  remove() {
    ++this.renderGeneration;
    this.token = null;
    if (this.widgetId !== undefined && window.turnstile) {
      try {
        window.turnstile.remove(this.widgetId);
      } catch {
        // The widget may already have been removed by Turnstile.
      }
      this.widgetId = undefined;
    }
  }
  isReady() {
    return typeof window.turnstile !== 'undefined';
  }
  reportError(translationKey) {
    this.token = null;
    this.errorCallback({
      type: 'error',
      content: app.translator.trans(translationKey)
    });
  }
}
flarum.reg.add('flectar-turnstile', 'common/states/TurnstileState', TurnstileState);